/**
 * ТИП ВОРОНКИ ЭТОГО ЧЕЛОВЕКА — 2026-09-21. Читается из cookie `funnel`, которую ставит дверь на
 * сервере (server/src/ab/funnel.ts), один раз при старте сборки, и дальше метит собой каждое событие
 * Amplitude, каждый вызов пикселя FB и GA.
 *
 * Не из окружения сборки, как плечо A/B (VITE_AB_ARM): воронок будет четыре и больше, а сборка одна.
 * Не назначается в браузере: монету бросает сервер, чтобы решение было одно на человека и не
 * зависело от того, успел ли выполниться скрипт.
 *
 * Нет cookie — воронка 1: это единственный путь, который существовал до этой правки, и все события до
 * неё честно читаются как первая воронка. Amplitude свойства назад не дописывает, поэтому у событий
 * до выкатки будет (none), а не 1 — отсчёт по воронкам начинается с выкатки.
 *
 * СПЛИТ-ТЕСТ ВОРОНОК — 2026-09-28 (Дима): кампания B несёт `?r=b`, кампания A — чистый `/` (пул по умолчанию); сервер бросает монету ВНУТРИ
 * пула и рядом с `funnel=N` ставит вторую cookie `funnel_split=<тест>.<пул>` (например
 * `Funnel_1st_Test.a`), а при `?v=<буква>` от QA — `funnel_split=override`. Органика второй cookie не
 * получает никогда. Зачем она клиенту: воронку 1 из кампании A, воронку 1 из органики и проверку QA по
 * одной `funnel=1` не различить, а это знаменатель сравнения 1 против 3.
 */

/**
 * Имена рядом с номерами — по той же причине, что у шагов онбординга (onboardSteps.ts): номер
 * упорядочивает воронку в отчёте, имя переживает перенумерацию и читается через год без расшифровки.
 *   1 current — текущая: лендинг → онбординг → контакт → выдача → пейволл → оплата
 *   2 refine  — та же, но после контакта человек уточняет фильтры выдачи вопросами
 *   3 immerse — та же, но вместо пейволла на выдаче погружение в продукт, пейволл внутри
 *   4 saas    — регистрация → сразу в продукт → апсейл внутри
 */
export const FUNNEL_NAMES: Readonly<Record<number, string>> = {
  1: 'current',
  2: 'refine',
  3: 'immerse',
  4: 'saas',
}

const DEFAULT_FUNNEL = 1

/** Как воронка назначена этому браузеру — всё, что клиент знает из двух cookie двери. */
export type FunnelAssignment = {
  type: number
  name: string
  /** Имя теста, если воронку выбрала монета пула этого теста; иначе null (органика, QA). */
  test: string | null
  /** Пул теста (сегодня `a` | `b`, набор задаёт FUNNEL_SPLITS на сервере); иначе null. */
  pool: string | null
  /** Воронку навязал `?v=<буква>` — это проверка, а не участник теста. */
  override: boolean
}

/* ТЕ ЖЕ ПРАВИЛА, ЧТО У СЕРВЕРА (server/src/ab/funnelConfig.ts TEST_NAME и POOL_NAME, funnel.ts
   validSplit): cookie пишет он, и клиент обязан признавать ровно то, что сервер пишет и сам читает
   обратно в прогон. Строже — и участник теста, которого сервер записал в `runs.keywords`, пришёл бы в
   Amplitude без метки; мягче — и клиент метил бы тестом то, что сервер тестом не считает. Имя теста
   становится ИМЕНЕМ СВОЙСТВА в Amplitude — отсюда без пробелов и точек (точка отделяет тест от пула).
   Значение не декодируется по той же причине: сервер пишет и читает его как есть. Кривое значение
   читается как «теста нет»: лучше потерять метку одного человека, чем завести свойство с мусорным
   именем. */
/* Первой — буква, как у сервера с 2026-09-28: имя с цифры сервер отклоняет при старте. */
const TEST_NAME = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/
const POOL_NAME = /^[a-z0-9]{1,16}$/

const cookieValue = (cookie: string, name: string): string | null => {
  const match = cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`))
  return match ? (match[1] ?? '') : null
}

/**
 * Разбор cookie двери. Чистая функция от строки `document.cookie`, чтобы разбор проверялся без
 * браузера (utils/funnel.test.ts).
 *
 * Пул засчитывается ТОЛЬКО при живой cookie `funnel`: без неё номер воронки — умолчание (1), и метка
 * «пул a, воронка 1» приписала бы человеку воронку, которой ему никто не назначал.
 */
export const readFunnelAssignment = (cookie: string): FunnelAssignment => {
  const rawType = cookie.match(/(?:^|;\s*)funnel=(\d{1,2})(?:;|$)/)
  const parsed = rawType ? Number(rawType[1]) : Number.NaN
  const hasFunnel = Number.isInteger(parsed) && parsed >= 1
  const type = hasFunnel ? parsed : DEFAULT_FUNNEL
  const name = FUNNEL_NAMES[type] ?? `funnel${type}`
  const none = { type, name, test: null, pool: null, override: false }

  const split = hasFunnel ? cookieValue(cookie, 'funnel_split') : null
  if (!split) return none
  if (split === 'override') return { ...none, override: true }
  /* По последней точке — как validSplit на сервере; точек в имени теста он не допускает, так что
     последняя точка и есть единственная. */
  const dot = split.lastIndexOf('.')
  const test = dot > 0 ? split.slice(0, dot) : ''
  const pool = dot > 0 ? split.slice(dot + 1) : ''
  if (!TEST_NAME.test(test) || !POOL_NAME.test(pool)) return none
  return { ...none, test, pool }
}

const ASSIGNMENT: FunnelAssignment = readFunnelAssignment(
  typeof document === 'undefined' ? '' : document.cookie,
)

export const FUNNEL_TYPE: number = ASSIGNMENT.type
export const FUNNEL_NAME: string = ASSIGNMENT.name
/** Имя теста (`Funnel_1st_Test`) — только у назначенных пулом. */
export const FUNNEL_TEST: string | null = ASSIGNMENT.test
/** Пул теста (`a` | `b`) — только у назначенных пулом. */
export const FUNNEL_POOL: string | null = ASSIGNMENT.pool
/** Воронку навязал `?v=<буква>` (QA). */
export const FUNNEL_OVERRIDE: boolean = ASSIGNMENT.override
export const FUNNEL_ASSIGNMENT: FunnelAssignment = ASSIGNMENT

/**
 * Имена свойств, которые метка теста не имеет права перекрыть. Имя теста задаёт .env сервера, и тест
 * по имени `environment` или `variant` молча переписал бы соседнее свойство в каждом событии.
 */
const RESERVED = new Set([
  'environment', 'variant', 'experiment', 'ab_variant', 'ab_experiment', 'funnel_type',
  'funnel_name', 'funnel_test', 'split_pool', 'device', 'timezone', 'locale',
  'first_funnel_type', 'first_split_pool',
])

export type FunnelTags = {
  /** В КАЖДОЕ событие Amplitude — и как свойство события, и в `$set` пользователя. */
  props: Record<string, string | number>
  /** В `$setOnce`: первое назначение, по нему отсеиваются перешедшие между кампаниями. */
  once: Record<string, string | number>
  /** В каждый вызов FB и GA. */
  params: Record<string, string | number>
}

/**
 * МЕТКИ ВОРОНКИ ДЛЯ ТРЁХ ПОТРЕБИТЕЛЕЙ — одна чистая функция, чтобы Amplitude, пиксель и GA не могли
 * разойтись в том, кто участник теста.
 *
 * Участник теста (назначен пулом) несёт СВОЙСТВО, НАЗВАННОЕ ИМЕНЕМ ТЕСТА, со значением-номером
 * воронки: `Funnel_1st_Test: 3`. Та же идея, что `onboarding_1stA/B: a` (utils/abVariant.ts): тест
 * ищется в Amplitude по своему имени и режется одним group-by, а всё, что в тест не входит, в этот
 * срез не попадает вовсе. Поэтому QA (`override`) получает только `split_pool: 'override'` и НЕ
 * получает свойства теста: проверки на проде не должны садиться в знаменатель.
 *
 * FB и GA получают `funnel_test` и `split_pool`, но не свойство по имени теста: в GA каждое имя
 * параметра регистрируется в Admin отдельно, и имя, меняющееся с каждым тестом, означало бы новую
 * регистрацию на каждый тест; кастомной конверсии Меты хватает пары «тест × пул».
 *
 * `first_split_pool` у органики — строка `none`, а не null: `$setOnce` с null в Amplitude либо
 * пропускается, либо ставит пустое значение навсегда, и ни то ни другое не читается как «пришёл сам».
 */
export const funnelTags = (a: FunnelAssignment): FunnelTags => {
  const base = { funnel_type: a.type, funnel_name: a.name }
  const pooled = a.test != null && a.pool != null
  const split: Record<string, string | number> = pooled
    ? { funnel_test: a.test as string, split_pool: a.pool as string }
    : a.override
      ? { split_pool: 'override' }
      : {}
  const named: Record<string, number> =
    pooled && !RESERVED.has(a.test as string) ? { [a.test as string]: a.type } : {}
  return {
    props: { ...base, ...split, ...named },
    once: {
      first_funnel_type: a.type,
      first_split_pool: pooled ? (a.pool as string) : a.override ? 'override' : 'none',
    },
    params: { ...base, ...split },
  }
}

export const FUNNEL_TAGS: FunnelTags = funnelTags(ASSIGNMENT)

/** Одним объектом, чтобы каждый вызов пикселя подмешивал одно и то же и ничего не забывал. */
export const FUNNEL_PARAMS: Readonly<Record<string, string | number>> = FUNNEL_TAGS.params

/** Что браузер запомнил о прошлом назначении — тройка «воронка × тест × пул». */
export type StoredAssignment = { funnel: number; test: string | null; pool: string | null }

export type AssignmentReason = 'new' | 'override' | 'repool' | 'recoin'

/**
 * СМЕНА НАЗНАЧЕНИЯ — для события `funnel_assigned` (utils/analytics.ts). Чистая функция: сравнивает
 * тройку этой загрузки с запомненной и говорит, случилось ли назначение, и почему.
 *
 *   override — QA навязал воронку `?v=<буква>` (и в браузере, который назначения ещё не видел);
 *   new      — браузер назначения ещё не видел;
 *   repool   — сменился тест или пул: клик по объявлению другой кампании, органика → пул и обратно;
 *   recoin   — тот же тест и пул, другая воронка: cookie стёрта и монета брошена заново.
 *
 * Причину сервера (`sticky` / `new` / `repool`) клиент не видит — cookie её не несёт, — поэтому
 * здесь она выводится из того, что браузер помнит; расходится с серверной только у того, кто стёр
 * localStorage, и тогда это честное «new».
 */
export const assignmentChange = (
  stored: StoredAssignment | null,
  current: FunnelAssignment,
): { next: StoredAssignment; props: Record<string, string | number | null> } | null => {
  const pool = current.override ? 'override' : current.pool
  const next: StoredAssignment = { funnel: current.type, test: current.test, pool }
  const same =
    stored != null && stored.funnel === next.funnel && stored.test === next.test && stored.pool === next.pool
  if (same) return null
  /* override — первым, как у сервера (funnelFor): у QA причина назначения — `?v=<буква>`, даже в
     браузере, который назначения ещё не видел; иначе первый заход проверки читался бы как `new` и
     садился в знаменатель «новых» рядом с рекламой. */
  const reason: AssignmentReason = current.override
    ? 'override'
    : !stored
      ? 'new'
      : stored.test !== next.test || stored.pool !== next.pool
        ? 'repool'
        : 'recoin'
  return {
    next,
    props: {
      funnel_type: current.type,
      funnel_name: current.name,
      funnel_test: current.test,
      split_pool: pool,
      previous_funnel: stored?.funnel ?? null,
      previous_pool: stored?.pool ?? null,
      reason,
    },
  }
}
